import React, { useCallback, useEffect, useState } from "react";
import { logError } from "@/lib/logger";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import ShareJobModal from "@/components/ShareJobModal";
import { Briefcase, ArrowRight, Check, MapPin, ShareNetwork } from "@phosphor-icons/react";

export default function CandidateJobs() {
    const [jobs, setJobs] = useState([]);
    const [applying, setApplying] = useState(null);
    const [err, setErr] = useState("");
    const [myReferralCode, setMyReferralCode] = useState(null);
    const [shareJob, setShareJob] = useState(null);

    const load = useCallback(
        () =>
            api
                .get("/me/jobs")
                .then((r) => setJobs(r.data))
                .catch((e) => logError("Failed to load jobs:", e)),
        [],
    );

    useEffect(() => {
        load();
        api
            .get("/me/candidate/referrals")
            .then((r) => setMyReferralCode(r.data?.referral_code || null))
            .catch(() => {});
    }, [load]);

    const apply = async (jobId) => {
        setErr("");
        setApplying(jobId);
        try {
            await api.post(`/me/jobs/${jobId}/apply`);
            await load();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setApplying(null);
        }
    };

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1100px] mx-auto" data-testid="candidate-jobs-page">
            <div className="mb-8 sm:mb-10">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                    Open requirements
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Browse roles.
                </h1>
            </div>

            {err && (
                <div
                    data-testid="apply-error"
                    className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3 mb-4"
                >
                    {err}{" "}
                    {err.includes("resume") && (
                        <Link
                            to="/candidate/profile"
                            className="font-bold underline ml-1"
                            data-testid="goto-profile-from-error"
                        >
                            Go to profile →
                        </Link>
                    )}
                </div>
            )}

            {jobs.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] p-10 text-center">
                    <Briefcase size={36} weight="bold" className="mx-auto text-[#A3A3A3] mb-4" />
                    <p className="font-heading text-xl font-bold tracking-tight mb-1">
                        No open roles right now
                    </p>
                    <p className="text-sm text-[#525252]">Check back soon.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {jobs.map((j) => (
                        <div
                            key={j.id}
                            data-testid={`cand-job-card-${j.id}`}
                            className="bg-white border border-[#E5E5E5] p-6 flex flex-col gap-3"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <span className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                    {j.department || "General"}
                                </span>
                                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-[#ECFDF5] text-[#059669]">
                                    Open
                                </span>
                            </div>
                            <h3 className="font-heading text-xl font-bold tracking-tight text-ink">
                                {j.title}
                            </h3>
                            <div className="flex flex-wrap gap-2 text-xs text-[#525252]">
                                <span className="inline-flex items-center gap-1">
                                    <MapPin size={12} weight="bold" /> {j.location || "Remote"}
                                </span>
                                <span>· {j.employment_type}</span>
                                <span>· {j.seniority}</span>
                                {j.salary_range && (
                                    <span className="font-bold text-ink">· {j.salary_range}</span>
                                )}
                            </div>
                            <p className="text-sm text-[#525252] line-clamp-3 flex-1">
                                {j.description}
                            </p>
                            <div className="pt-3 border-t border-[#E5E5E5] flex items-center gap-2">
                                {j.already_applied ? (
                                    <span
                                        data-testid={`already-applied-${j.id}`}
                                        className="inline-flex items-center gap-2 text-sm font-bold text-[#059669] flex-1"
                                    >
                                        <Check size={14} weight="bold" /> Applied
                                    </span>
                                ) : (
                                    <button
                                        data-testid={`apply-button-${j.id}`}
                                        onClick={() => apply(j.id)}
                                        disabled={applying === j.id}
                                        className="flex-1 bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-4 py-2.5 font-bold tracking-wide text-sm flex items-center justify-center gap-2 transition-colors"
                                    >
                                        {applying === j.id ? "Applying…" : "Apply now"}
                                        <ArrowRight size={14} weight="bold" />
                                    </button>
                                )}
                                <button
                                    data-testid={`share-button-${j.id}`}
                                    onClick={() =>
                                        setShareJob({
                                            id: j.id,
                                            title: j.title,
                                            clientName: j.client?.name,
                                            salary: j.salary_range,
                                            location: j.location,
                                        })
                                    }
                                    title="Refer this role — earn ₹5,000 if your friend gets hired, ₹100 when they reach interview stage."
                                    className="border border-[#E5E5E5] hover:border-ink text-ink px-3 py-2.5 font-bold text-sm inline-flex items-center justify-center gap-1.5 transition-colors"
                                >
                                    <ShareNetwork size={14} weight="bold" />
                                    <span className="hidden sm:inline">Refer</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {shareJob && (
                <ShareJobModal
                    open={!!shareJob}
                    onClose={() => setShareJob(null)}
                    jobId={shareJob.id}
                    jobTitle={shareJob.title}
                    clientName={shareJob.clientName}
                    salary={shareJob.salary}
                    location={shareJob.location}
                    refCode={myReferralCode}
                />
            )}
        </div>
    );
}
